import React, { useEffect, useState } from 'react';
import {
  Alert, Button, DatePicker, Form, Modal, Select, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import TimeSelect, { DEFAULT_START, DEFAULT_END } from './TimeSelect';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function parseHHMM(str) {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  return dayjs().startOf('day').hour(h).minute(m);
}

const REASON_OPTIONS = [
  'Sick/Health Related', 'Jury Duty', 'Bereavement', 'Outage', 'Other',
].map((v) => ({ label: v, value: v }));

const PERIOD_OPTIONS = [
  'Entire Day', 'Absent Part of Day',
].map((v) => ({ label: v, value: v }));

const INSTRUCTIONS = `When to use this form:
• Same Day Absence — any reason
• Absence occurring tomorrow for a shift before 9am — any reason
• Non-Sick/Health related Absence occurring in next 21 days
• Bereavement / Jury Duty Absence

When to instead create a Paycor Time Off Request — all other scenarios including:
• Normal PTO requests (e.g. 21 day advance notice)
• Sick/Health related Absence occurring later than tomorrow 9am`;

const NOTES = `Note:
If you are calling out for a same-day absence (full or partial) during an anchor shift or after hours shift, you must also notify the On-Call at 947-224-4088 Option 2. This step is mandatory to ensure immediate coverage can be arranged.

On-Call Hours:
MONDAY – FRIDAY 8PM–8AM
SATURDAY 8AM–8AM (24 HOUR COVERAGE)
SUNDAY 8AM–8AM (24 HOUR COVERAGE)

Outside of these hours, you do not need to call the on-call line. Fill out this absence form only.`;

export default function AbsenceCreateModal({
  open,
  userId,
  userName,
  initialTeamId,
  initialTeamName,
  currentUser,
  navigate,
  onClose,
  onCreated,
}) {
  const internalNavigate = useNavigate();
  const nav = navigate || internalNavigate;

  const isAdmin   = currentUser?.Role === 'admin';
  const canEdit   = (currentUser?.CanEditAbsences ?? 0) >= 2;
  const minDate   = isAdmin ? null : canEdit ? dayjs().subtract(7, 'day').startOf('day') : dayjs().startOf('day');
  const [form]          = Form.useForm();
  const [saving,          setSaving]          = useState(false);
  const [period,          setPeriod]          = useState('');
  const [allUsers,        setAllUsers]        = useState([]);
  const [allTeams,        setAllTeams]        = useState([]);
  const [employeeHasTeam,  setEmployeeHasTeam]  = useState(false);
  const [employeeSelected, setEmployeeSelected] = useState(false);
  const [shiftSuggestion, setShiftSuggestion] = useState(null);

  const fetchShift = async (date, uid, teamId) => {
    setShiftSuggestion(null);
    if (!date || !uid || !teamId) return;
    try {
      const res = await api.get('/api/schedule/shifts', {
        params: { team_ids: teamId, date: date.format('YYYY-MM-DD') },
      });
      const shift = (res.data || []).find((s) => s.UserID === uid);
      if (shift?.ShiftStart && shift?.ShiftEnd) {
        setShiftSuggestion({
          start: parseHHMM(shift.ShiftStart),
          end:   parseHHMM(shift.ShiftEnd),
          label: `${parseHHMM(shift.ShiftStart).format('h:mm A')} – ${parseHHMM(shift.ShiftEnd).format('h:mm A')}`,
        });
      }
    } catch { /* no shift data — silently skip */ }
  };

  const needsEmployeeSelect = !userId;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setPeriod('');

    if (needsEmployeeSelect) {
      // Admin creating on behalf — show employee selector
      setEmployeeHasTeam(false);
      setEmployeeSelected(false);
      if (!allUsers.length) {
        api.get('/api/absences/employees').then((r) => {
          setAllUsers(
            r.data.map((u) => ({
              label:    u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
              value:    u.ID,
              teamName: u.TeamName || null,
              teamId:   u.TeamID   || null,
            }))
          );
        }).catch(() => message.error('Failed to load users.'));
      }
    }

    // Load teams, then resolve initial team name
    const teamsPromise = allTeams.length
      ? Promise.resolve(allTeams)
      : api.get('/api/teams').then((r) => { const t = r.data || []; setAllTeams(t); return t; }).catch(() => []);

    if (!needsEmployeeSelect) {
      teamsPromise.then((teams) => {
        let teamId = initialTeamId || null;
        if (!teamId && initialTeamName) {
          teamId = teams.find((t) => t.Name === initialTeamName)?.ID || null;
        }
        if (!teamId && currentUser?.TeamID) {
          teamId = currentUser.TeamID;
        }
        form.setFieldsValue({ team_id: teamId || undefined });
        setEmployeeHasTeam(!!teamId);
      });
    }
  }, [open]); // eslint-disable-line

  const handleCreate = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        user_id:        userId ?? values.user_id,
        reason:         values.reason,
        absence_period: values.absence_period,
        date_start:     values.date_start?.format('YYYY-MM-DD'),
        date_end:       values.date_end?.format('YYYY-MM-DD') || null,
        start_time:     values.start_time?.format('h:mm A') || null,
        end_time:       values.end_time?.format('h:mm A') || null,
        team_id:        values.team_id || null,
      };
      const res = await api.post('/api/absences', body);
      const ids = res.data.ids || [res.data.id];
      // On-call: the team's message, when the server says on-call must be told.
      if (res.data.on_call_message) {
        Modal.warning({
          title: 'Call On-Call',
          content: <div style={{ whiteSpace: 'pre-wrap' }}>{res.data.on_call_message}</div>,
          width: 520,
        });
      }
      if (ids.length > 1) {
        message.success(`${ids.length} absence notifications submitted.`);
      } else {
        message.success('Absence notification submitted.');
      }
      onClose();
      if (onCreated) {
        onCreated(ids[0]);
      } else if (ids.length > 1) {
        nav(userId ? '/absences' : '/my-absences');
      } else {
        nav(`/absences/${ids[0]}`);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to submit absence notification.');
    } finally {
      setSaving(false);
    }
  };

  // /api/absences/employees already gives just the Visible Teams' people and
  // those who also work for one of them (Admin > Users, Also works for) --
  // filtering again by own team here would drop the latter.
  const visibleUserOptions = allUsers;

  const title = needsEmployeeSelect
    ? 'Submit Absence on Behalf of Employee'
    : userName
      ? `Submit Absence Notification — ${userName}`
      : 'Submit Absence Notification';

  return (
    <Modal maskClosable={false}
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleCreate}
      confirmLoading={saving}
      okText="Submit"
      destroyOnClose
      width={580}
    >
      <Alert
        type="info"
        style={{ marginTop: 8, marginBottom: 16 }}
        message={
          <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: 13 }}>
            {INSTRUCTIONS}
          </pre>
        }
      />

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed) => {
          if (changed.start_time) {
            const start = changed.start_time;
            const end = form.getFieldValue('end_time');
            if (end && dayjs.isDayjs(end) && !end.isAfter(start)) {
              form.setFieldValue('end_time', start.add(30, 'minute'));
            }
          }
        }}
      >
        {needsEmployeeSelect && (
          <Form.Item label="Employee" name="user_id" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder="Select employee…"
              options={visibleUserOptions}
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              onChange={(id) => {
                setEmployeeSelected(!!id);
                const opt = id ? allUsers.find((u) => u.value === id) : null;
                form.setFieldValue('team_id', opt?.teamId || undefined);
                setEmployeeHasTeam(!!opt?.teamId);
              }}
            />
          </Form.Item>
        )}

        <Form.Item label="Team" name="team_id" rules={[{ required: true, message: 'Required' }]}>
          <Select
            showSearch placeholder="Select team…"
            options={allTeams.map((t) => ({ label: t.Name, value: t.ID }))}
            filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
            disabled={needsEmployeeSelect ? (!employeeSelected || employeeHasTeam) : employeeHasTeam}
          />
        </Form.Item>

        <Form.Item label="Reason" name="reason" rules={[{ required: true, message: 'Required' }]}>
          <Select options={REASON_OPTIONS} placeholder="Select reason…" />
        </Form.Item>

        <Form.Item label="Absence Period" name="absence_period" rules={[{ required: true, message: 'Required' }]}>
          <Select
            options={PERIOD_OPTIONS}
            placeholder="Select period…"
            onChange={(v) => {
              setPeriod(v);
              form.setFieldsValue({ date_end: undefined, start_time: DEFAULT_START, end_time: DEFAULT_END });
            }}
          />
        </Form.Item>
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16, fontSize: 12 }}>
          If you are calling out for multiple shifts in the same day, please create an absence notification for each shift.
        </Typography.Text>

        <Form.Item label="Date Start" name="date_start" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker
            style={{ width: '100%' }} format="YYYY-MM-DD"
            disabledDate={(d) => d && (minDate ? d < minDate : false) || d > dayjs().add(21, 'day').endOf('day')}
            onChange={(date) => {
              setShiftSuggestion(null);
              const uid    = userId ?? form.getFieldValue('user_id');
              const teamId = form.getFieldValue('team_id');
              fetchShift(date, uid, teamId);
            }}
          />
        </Form.Item>
        {/* Shown to everyone, including the roles whose picker does allow some
            past dates: the deadline it describes is the policy, not the
            calendar, and someone filing on an agent's behalf needs to know it
            as much as the agent does.

            Styled as the Absence Period note above it, so the form has one
            kind of hint rather than two. */}
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16, fontSize: 12 }}>
          Past dates cannot be selected. If your call-out is for a date that has already
          passed, it is past the required submission deadline &mdash; please contact your
          Supervisor immediately.
        </Typography.Text>

        {shiftSuggestion && (
          <Form.Item style={{ marginTop: -8 }}>
            <Alert
              type="info"
              style={{ padding: '6px 12px' }}
              message={
                <span style={{ fontSize: 13 }}>
                  Scheduled shift: <strong>{shiftSuggestion.label}</strong>
                  {period && (
                    <Button
                      size="small" type="link" style={{ padding: '0 6px' }}
                      onClick={() => form.setFieldsValue({ start_time: shiftSuggestion.start, end_time: shiftSuggestion.end })}
                    >
                      Use these times
                    </Button>
                  )}
                  {!period && <span style={{ color: 'var(--ant-color-text-secondary)', marginLeft: 8, fontSize: 12 }}>select a period above to apply</span>}
                </span>
              }
            />
          </Form.Item>
        )}

        {period === 'Entire Day' && (
          <>
            <Form.Item label="Shift Start Time (EST)" name="start_time" rules={[{ required: true, message: 'Required' }]}>
              <TimeSelect />
            </Form.Item>
            <Form.Item label="Shift End Time (EST)" name="end_time" rules={[{ required: true, message: 'Required' }]}>
              <TimeSelect />
            </Form.Item>
          </>
        )}

        {period === 'Absent Part of Day' && (
          <>
            <Form.Item label="Start Time of Absence (EST)" name="start_time" rules={[{ required: true, message: 'Required' }]}>
              <TimeSelect />
            </Form.Item>
            <Form.Item label="End Time of Absence (EST)" name="end_time" rules={[{ required: true, message: 'Required' }]}>
              <TimeSelect />
            </Form.Item>
          </>
        )}
      </Form>

      <Alert
        type="warning"
        style={{ marginTop: 8 }}
        message={
          <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: 13 }}>
            {NOTES}
          </pre>
        }
      />
    </Modal>
  );
}
